import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import tourSteps from "./tourSteps";
import "./AdminTour.css";

const STORAGE_KEY = "admin_tour_state";
const AUTO_STARTED_KEY = "admin_tour_auto_started";
const TARGET_POLL_MS = 150;
const TARGET_POLL_MAX_TRIES = 30; // ~4.5s — long enough for theme data to load after navigating
const START_EVENT = "admin-tour-start";

// A real inline button each page renders itself, right in its own header next to its other
// buttons — NOT a floating button, and NOT portaled into a slot element this component has to go
// hunting for in the DOM. That's what this used to do, and both ways of detecting the slot
// (MutationObserver, then polling) turned out unreliable in practice — the button could silently
// never appear. Dispatching a plain event and letting <AdminTour /> (mounted once, near the root)
// react to it needs no DOM lookup of any kind, so it can't have that failure mode.
export function TourStartButton({ className = "admin-tour-fab" }) {
  return (
    <button type="button" className={className} onClick={() => window.dispatchEvent(new Event(START_EVENT))}>
      <i className="fa-solid fa-circle-question" aria-hidden="true" /> Take a tour
    </button>
  );
}

function loadState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed.stepIndex === "number" ? parsed : null;
  } catch {
    return null;
  }
}

function persistState(state) {
  try {
    if (state) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

// sessionStorage (not a ref) so a hard refresh — which remounts everything — doesn't restart the
// tour again on its own; a genuinely new tab/session still gets the auto-start.
function hasAutoStarted() {
  try {
    return sessionStorage.getItem(AUTO_STARTED_KEY) === "1";
  } catch {
    return false;
  }
}

function markAutoStarted() {
  try {
    sessionStorage.setItem(AUTO_STARTED_KEY, "1");
  } catch {
    /* ignore */
  }
}

function computeCardStyle(rect) {
  const margin = 16;
  const cardWidth = 320;
  const estCardHeight = 220;
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  let top = rect.bottom + margin;
  if (top + estCardHeight > viewportH) {
    top = Math.max(margin, rect.top - estCardHeight - margin);
  }
  let left = rect.left;
  if (left + cardWidth + margin > viewportW) {
    left = Math.max(margin, viewportW - cardWidth - margin);
  }
  return { top, left };
}

/**
 * Guided walkthrough across every admin page (themes dashboard → Studio wizard → Live Host).
 * Step position is persisted to sessionStorage, and Next/Back navigate to the right route
 * automatically when a step lives on a different page than the current one. For steps inside the
 * Studio wizard (which never changes route across its 7 steps), a step can also carry a
 * `wizardStep` — dispatched as a custom event studio.jsx listens for — so the tour can flip the
 * wizard to the right page before it looks for that step's target element.
 */
export default function AdminTour() {
  const location = useLocation();
  const navigate = useNavigate();

  const initial = loadState();
  const [active, setActive] = useState(Boolean(initial?.active));
  const [stepIndex, setStepIndex] = useState(initial?.stepIndex ?? 0);
  const [rect, setRect] = useState(null);

  const step = active ? tourSteps[stepIndex] : null;
  const onCorrectPage = Boolean(step) && location.pathname === step.page;

  useEffect(() => {
    persistState(active ? { active: true, stepIndex } : null);
  }, [active, stepIndex]);

  // Auto-start once per browser tab session when an admin first lands on the tour's starting
  // page (the rules/Studio page). Tracked in sessionStorage rather than a mount ref, so a hard
  // refresh — which remounts this component and would otherwise look just like a fresh arrival —
  // doesn't keep restarting it every time; closing the tab (or the "Take a tour" button) is what
  // lets it run again.
  useEffect(() => {
    if (hasAutoStarted()) return;
    if (location.pathname !== tourSteps[0].page) return;
    markAutoStarted();
    if (active) return;
    setStepIndex(0);
    setActive(true);
  }, [active, location.pathname]);

  const goToStep = useCallback(
    (index) => {
      if (index < 0) return;
      if (index >= tourSteps.length) {
        setActive(false);
        return;
      }
      const target = tourSteps[index];
      setStepIndex(index);
      if (target.page !== location.pathname) {
        navigate(target.page);
      }
    },
    [location.pathname, navigate]
  );

  // Tell the Studio wizard which of its 7 pages to show, for steps that need it.
  useEffect(() => {
    if (!active || !step || !onCorrectPage) return;
    if (typeof step.wizardStep === "number") {
      window.dispatchEvent(new CustomEvent("admin-tour-goto-wizard-step", { detail: step.wizardStep }));
    }
  }, [active, step, onCorrectPage]);

  // Locate the current step's target element, re-locating on resize/scroll, and auto-advancing
  // past a step whose element never shows up (e.g. the settings-icon step when no hunt exists
  // yet) instead of leaving the admin stuck with nothing to point at.
  useEffect(() => {
    setRect(null);
    if (!active || !step || !onCorrectPage) return undefined;

    let tries = 0;
    let cleanupListeners = null;
    let cancelled = false;

    const attach = (el) => {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      const update = () => setRect(el.getBoundingClientRect());
      update();
      window.addEventListener("resize", update);
      window.addEventListener("scroll", update, true);
      return () => {
        window.removeEventListener("resize", update);
        window.removeEventListener("scroll", update, true);
      };
    };

    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-tour="${step.target}"]`);
      if (el) {
        cleanupListeners = attach(el);
        return;
      }
      tries += 1;
      if (tries >= TARGET_POLL_MAX_TRIES) {
        goToStep(stepIndex + 1);
        return;
      }
      timeoutId = window.setTimeout(tick, TARGET_POLL_MS);
    };

    let timeoutId = window.setTimeout(tick, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      if (cleanupListeners) cleanupListeners();
    };
  }, [active, step, onCorrectPage, goToStep, stepIndex]);

  const handleStart = useCallback(() => {
    setActive(true);
    goToStep(0);
  }, [goToStep]);

  useEffect(() => {
    window.addEventListener(START_EVENT, handleStart);
    return () => window.removeEventListener(START_EVENT, handleStart);
  }, [handleStart]);

  const handleNext = useCallback(() => goToStep(stepIndex + 1), [goToStep, stepIndex]);
  const handleBack = useCallback(() => goToStep(stepIndex - 1), [goToStep, stepIndex]);
  const handleSkip = useCallback(() => setActive(false), []);

  return (
    <>
      {active && step && onCorrectPage ? (
        <div className="admin-tour-overlay">
          {rect ? (
            <div
              className="admin-tour-highlight"
              style={{
                top: rect.top - 6,
                left: rect.left - 6,
                width: rect.width + 12,
                height: rect.height + 12,
              }}
            />
          ) : null}

          <div
            className={`admin-tour-card${rect ? "" : " admin-tour-card--centered"}`}
            style={rect ? computeCardStyle(rect) : undefined}
          >
            {rect ? (
              <>
                <div className="admin-tour-card-step">
                  Step {stepIndex + 1} of {tourSteps.length}
                </div>
                <h4 className="admin-tour-card-title">{step.title}</h4>
                <p className="admin-tour-card-desc">{step.description}</p>
              </>
            ) : (
              <p className="admin-tour-card-desc">Loading this step…</p>
            )}
            <div className="admin-tour-card-actions">
              <button type="button" className="admin-tour-btn admin-tour-btn--ghost" onClick={handleSkip}>
                Skip
              </button>
              <div className="admin-tour-card-actions-right">
                <button type="button" className="admin-tour-btn" onClick={handleBack} disabled={stepIndex === 0}>
                  Back
                </button>
                <button type="button" className="admin-tour-btn admin-tour-btn--primary" onClick={handleNext}>
                  {stepIndex === tourSteps.length - 1 ? "Finish" : "Next"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
